import React, { useEffect, useState } from 'react';
import {
  Box, Container, Paper, Typography, TextField, Button, Stack, Avatar, Divider, Alert, CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon, Email as EmailIcon, Business as BusinessIcon, Phone as PhoneIcon,
} from '@mui/icons-material';
import { updateUserProfile } from '../api/user.api';

function UserProfile() {
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', role: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Try to load user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      setForm({
        firstName: parsed.firstName || '',
        lastName: parsed.lastName || '',
        email: parsed.email || '',
        phone: parsed.phone || '',
        company: parsed.companyName || parsed.company || '',
        role: parsed.role || '',
      });
    }
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔄 Saving profile with data:', {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        company: form.company,
      });

      const { response, err } = await updateUserProfile({
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        company: form.company,
      });

      console.log('📡 API Response:', { response, err });

      if (err) {
        console.error('❌ Profile update error:', err);
        setError(err.response?.data?.message || 'Failed to update profile');
        return;
      }

      if (response?.message && response?.user) {
        console.log('✅ Profile updated successfully:', response.user);
        // Update local state with the response from server
        const updatedUser = { ...user, ...response.user };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));

        setEditMode(false);
        setMessage('Profile updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else if (response?.message) {
        // Handle case where only message is returned
        console.log('✅ Profile updated successfully:', response.message);
        setEditMode(false);
        setMessage('Profile updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        setError('Unexpected response from server');
      }
    } catch (err) {
      console.error('💥 Profile update exception:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <Typography variant="h6">No user profile found.</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{
            bgcolor: 'primary.main', width: 64, height: 64, mr: 3,
          }}
          >
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {form.firstName}
              {' '}
              {form.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {form.role}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={3}>
          <TextField
            label="First Name"
            value={form.firstName}
            onChange={handleChange('firstName')}
            disabled={!editMode || loading}
            InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <TextField
            label="Last Name"
            value={form.lastName}
            onChange={handleChange('lastName')}
            disabled={!editMode || loading}
          />
          <TextField
            label="Email"
            value={form.email}
            disabled
            InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={handleChange('phone')}
            disabled={!editMode || loading}
            InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <TextField
            label="Company"
            value={form.company}
            onChange={handleChange('company')}
            disabled={!editMode || loading}
            InputProps={{ startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          {editMode ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditMode(false);
                  setError('');
                  // Reset form to original values
                  setForm({
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    company: user.companyName || user.company || '',
                    role: user.role || '',
                  });
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outlined" onClick={() => setEditMode(true)}>Edit Profile</Button>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

export default UserProfile;
